import { useTranslation } from 'next-i18next';
import { MouseEventHandler, ReactNode } from 'react';
import { dashify } from '@bridebook/toolbox';
import BridebookLogo from '@bridebook/ui/src/components/svg/bridebook-logo';
import IconFacebook from '@bridebook/ui/src/components/svg/icon-facebook';
import IconInstagram from '@bridebook/ui/src/components/svg/icon-instagram';
import IconPinterest from '@bridebook/ui/src/components/svg/icon-pinterest';
import IconTwitter from '@bridebook/ui/src/components/svg/icon-twitter';
import { IconTiktok, IconYoutube } from '@bridebook/ui/src/icons';
import LinkPipeList from './link-pipe-list';
import TrackLink from './track-link';

interface IProps {
  onSocialUrlClick?: MouseEventHandler<HTMLAnchorElement>;
  localeSelect: ReactNode;
}

const FooterBottom = ({ onSocialUrlClick, localeSelect }: IProps) => {
  const { t } = useTranslation('common');
  const year = new Date().getFullYear();
  const copyrights = `© ${year} Bridebook Ltd`;

  const TRACKED_LINKS_LIST: { url: string; title: string }[] = [
    { url: 'cookie-policy', title: t('footer.cookiePolicy') },
    { url: 'privacy-policy', title: t('footer.privacyPolicy') },
    { url: 'terms', title: t('footer.terms') },
  ];

  const SOCIAL_MEDIA_LIST: { url: string; icon: ReactNode }[] = [
    {
      url: 'https://www.youtube.com/c/BridebookUk/featured',
      icon: <IconYoutube color="mineShaft" width={16} />,
    },
    {
      url: 'https://www.tiktok.com/@bridebook',
      icon: <IconTiktok color="mineShaft" />,
    },
    {
      url: 'https://www.facebook.com/bridebookhq',
      icon: <IconFacebook color="mineShaft" width={7} />,
    },
    {
      url: 'https://www.pinterest.com/bridebook/',
      icon: <IconPinterest color="mineShaft" width={12} />,
    },
    {
      url: 'https://twitter.com/bridebook',
      icon: <IconTwitter color="mineShaft" width={14} />,
    },
    {
      url: 'https://www.instagram.com/bridebookhq/',
      icon: <IconInstagram color="mineShaft" width={15} />,
    },
  ];

  return (
    <div data-name="footer-bottom" className="bcbot">
      <div className="bccopy">
        <a href="/">
          <BridebookLogo color="space" width={129} />
        </a>
        <div>{copyrights}</div>
      </div>
      <div className="bcright">
        <div className="bclinks">
          <LinkPipeList
            list={TRACKED_LINKS_LIST.map((item) => (
              <TrackLink
                url={item.url}
                title={item.title}
                key={`footer-bottom-tracked-link-${dashify(item.title)}`}
              />
            ))}
          />
        </div>
        <div className="bcsocial">
          {SOCIAL_MEDIA_LIST.map((item) => (
            <a
              key={`footer-bottom-social-${item.url}`}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              onClick={onSocialUrlClick}>
              {item.icon}
            </a>
          ))}
        </div>
        {localeSelect && <div className="bclocale">{localeSelect}</div>}
      </div>
    </div>
  );
};

export default FooterBottom;
